import { LogicContext } from '@/logic/main/types';
import { isLocalStorageAvailable } from '@/utils';

interface BaseNativeMessage {
  Func:
    | 'LinksToOpenInExternal'
    | 'InitHybridApplication'
    | 'ShowRatingPopUp'
    | 'EnablePush'
    | 'SetAlias';
}

interface LinksToOpenInExternalMessage extends BaseNativeMessage {
  Func: 'LinksToOpenInExternal';
  links: readonly string[];
}

interface InitHybridApplicationMessage extends BaseNativeMessage {
  Func: 'InitHybridApplication';
  externalLinks: readonly string[];
  excludeLinksfromExternal: readonly string[];
  secondWebViewLinks: readonly string[];
}

interface ShowRatingPopUpMessage extends BaseNativeMessage {
  Func: 'ShowRatingPopUp';
}

interface EnablePushMessage extends BaseNativeMessage {
  Func: 'EnablePush';
}

interface SetAliasMessage extends BaseNativeMessage {
  Func: 'SetAlias';
  alias: string;
}

type HybridApplicationMessage =
  | LinksToOpenInExternalMessage
  | InitHybridApplicationMessage
  | ShowRatingPopUpMessage
  | EnablePushMessage
  | SetAliasMessage;

const sendNativeMessage = (message: HybridApplicationMessage): void => {
  window.webkit?.messageHandlers?.cbCallBackHandlerCGP?.postMessage(
    JSON.stringify(message)
  );
};

const STORAGE_KEY = 'loginCount';

const EXTERNAL_LINKS = [
  'help.williamhill',
  'sports.williamhill',
  'custhelp.com',
] as const;

const EXCLUDE_LINKS_FROM_EXTERNAL = [
  '/mob/',
  '/mob_esp/',
  '/mob_ext/',
] as const;

const SECOND_WEB_VIEW_LINKS = ['/payment/deposit/'] as const;

const getCurrentLoginCount = (): number => {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    const parsedCount = parseInt(storedValue ?? '0', 10);

    return parsedCount >= 0 ? parsedCount : 0;
  } catch (error) {
    console.error('## Wrapper: Error reading from localStorage:', error);
    return 0;
  }
};

const setLoginCount = (count: number): void => {
  try {
    localStorage.setItem(STORAGE_KEY, count.toString());
  } catch (error) {
    console.error('## Wrapper: Error writing to localStorage:', error);
  }
};

/**
 * Posts a message to the native app to configure external link handling
 * @returns Promise<void> Resolves when the message is sent
 */
export const initializeHybridApplication = (): Promise<void> => {
  return new Promise(resolve => {
    try {
      sendNativeMessage({
        Func: 'LinksToOpenInExternal',
        links: EXTERNAL_LINKS,
      });

      sendNativeMessage({
        Func: 'InitHybridApplication',
        externalLinks: EXTERNAL_LINKS,
        excludeLinksfromExternal: EXCLUDE_LINKS_FROM_EXTERNAL,
        secondWebViewLinks: SECOND_WEB_VIEW_LINKS,
      });

      resolve();
    } catch (error) {
      console.error('## Wrapper: Error in initializeHybridApplication:', error);
      resolve(); // Still resolve to not break the state machine flow
    }
  });
};

/**
 * Increments the login count stored in localStorage
 * @returns Promise<number> The new login count
 */
export const incrementLoginCount = (): Promise<number> => {
  return new Promise(resolve => {
    try {
      if (!isLocalStorageAvailable()) {
        console.warn('## Wrapper: localStorage not available, using fallback');
        resolve(1);
        return;
      }

      const currentCount = getCurrentLoginCount();
      const newCount = currentCount + 1;

      setLoginCount(newCount);

      resolve(newCount);
    } catch (error) {
      console.error(
        '## Wrapper: Unexpected error in incrementLoginCount:',
        error
      );
      resolve(1);
    }
  });
};

/**
 * Shows the native rate me popup
 * @returns Promise<void> Resolves when the message is sent
 */
export const showRateMePopup = (): Promise<void> => {
  return new Promise(resolve => {
    try {
      sendNativeMessage({
        Func: 'ShowRatingPopUp',
      });
      resolve();
    } catch (error) {
      console.error('## Wrapper: Error in showRateMePopup:', error);
      resolve(); // Still resolve to not break the state machine flow
    }
  });
};

/**
 * Requests the native app to enable push notifications.
 * @returns Promise<void> Resolves when the message is sent.
 */
export const enablePushNotifications = (): Promise<void> => {
  return new Promise(resolve => {
    try {
      sendNativeMessage({
        Func: 'EnablePush',
      });
      resolve();
    } catch (error) {
      console.error('## Wrapper: Error in enablePushNotifications:', error);
      resolve(); // Still resolve to not break the state machine flow
    }
  });
};

/**
 * Sets the user alias in the native app using the userAccountNumber from context.
 * @param context LogicContext containing user information
 * @returns Promise<void> Resolves when the message is sent
 */
export const setUserAlias = (context: LogicContext): Promise<void> => {
  return new Promise(resolve => {
    try {
      if (context?.user?.userAccountNumber) {
        sendNativeMessage({
          Func: 'SetAlias',
          alias: context.user.userAccountNumber,
        });
      }
      resolve();
    } catch (error) {
      console.error('## Wrapper: Error in setUserAlias:', error);
      resolve(); // Still resolve to not break the state machine flow
    }
  });
};
